import {useCallback, useMemo, useState, type KeyboardEvent, type MouseEvent as ReactMouseEvent} from 'react'
import {Accordion, AccordionSummary, AccordionDetails, Typography, Box, Button, Menu, MenuItem} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BorderAllIcon from '@mui/icons-material/BorderAll'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  addSideEdgeFeature,
  removeSideEdgeFeature,
  updateSideEdgeFeature,
  clampSideFeaturePosition,
  addCustomDecal,
  removeCustomDecal,
  selectActiveScene,
} from '../../../common/components/store/scenesSlice'
import type {CustomDecal} from '../../../common/types/customDecalTypes'
import {generateCustomDecalId} from '../../../common/types/customDecalTypes'
import CustomDecalItem from './CustomDecalItem'
import {sealUndo} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {
  SideEdge,
  SideEdgeFeatureConfig,
  SideEdgeFeatureType,
  SideButtonFeature,
  MuteSwitchFeature,
  AntennaFeature,
  ScrewFeature,
  SideSpeakerGrillFeature,
  ChargingConnectorFeature,
  SimCardSlotFeature,
} from '../../../common/components/types'
import {setPreviewSideEdge, type PreviewSideEdge} from '../../../common/components/deviceMesh/previewSideEdge'
import {setRemovedSideEdge} from '../../../common/components/deviceMesh/removedSideEdgeStore'
import {SIDE_BUTTON_DEPTH} from '../../../common/components/workerGeo/buildSideButtons'
import SliderRow from '../sharedWidgets/SliderRow'

// ── Edges list ────────────────────────────────────────────────────

const EDGES: SideEdge[] = ['left', 'right', 'top', 'bottom']

// ── Feature types available for side edges ────────────────────────

const SIDE_FEATURE_TYPES: SideEdgeFeatureType[] = [
  'sideButton',
  'muteSwitch',
  'antenna',
  'screw',
  'sideSpeakerGrill',
  'chargingConnector',
  'simCardSlot',
]

// ── Default values for new features ───────────────────────────────

interface SideFeatureDefaultsMap {
  sideButton: Omit<SideButtonFeature, 'id'>
  muteSwitch: Omit<MuteSwitchFeature, 'id'>
  antenna: Omit<AntennaFeature, 'id'>
  screw: Omit<ScrewFeature, 'id'>
  sideSpeakerGrill: Omit<SideSpeakerGrillFeature, 'id'>
  chargingConnector: Omit<ChargingConnectorFeature, 'id'>
  simCardSlot: Omit<SimCardSlotFeature, 'id'>
}

const SIDE_FEATURE_DEFAULTS: SideFeatureDefaultsMap = {
  sideButton: {
    type: 'sideButton',
    position: 0,
    width: 2.5,
    height: 10,
    cornerRadius: 1.2,
    depth: 0.5,
    chamfer: 0,
  },
  muteSwitch: {
    type: 'muteSwitch',
    position: 0,
    width: 2.5,
    height: 5,
    cornerRadius: 1.0,
  },
  antenna: {type: 'antenna', position: 0, width: 1.0},
  screw: {type: 'screw', position: 0, diameter: 1.4, pattern: 'star'},
  sideSpeakerGrill: {type: 'sideSpeakerGrill', position: 0, width: 1.2, height: 1.2, cornerRadius: 0.6},
  chargingConnector: {type: 'chargingConnector', position: 0, width: 9.0, height: 3.5, cornerRadius: 1.5},
  simCardSlot: {type: 'simCardSlot', position: 0, width: 2.5, height: 12, cornerRadius: 1.0, pinHoleOffset: -4.5},
}

// ── Convert a feature config to preview store shape ───────────────

const featureToPreview = (edge: SideEdge, feature: SideEdgeFeatureConfig): PreviewSideEdge => {
  const base: PreviewSideEdge = {
    type: feature.type,
    edge,
    position: feature.position,
    width: 0,
    height: 0,
    cornerRadius: 0,
    diameter: 0,
    depth: 0,
    chamfer: 0,
    pinHoleOffset: 0,
  }
  switch (feature.type) {
    case 'sideButton':
      return {
        ...base,
        width: feature.width,
        height: feature.height,
        cornerRadius: feature.cornerRadius,
        depth: feature.depth ?? 0.5,
        chamfer: feature.chamfer ?? 0,
      }
    case 'muteSwitch':
      return {
        ...base,
        width: feature.width,
        height: feature.height,
        cornerRadius: feature.cornerRadius,
      }
    case 'antenna':
      return {...base, width: feature.width}
    case 'screw':
      return {...base, diameter: feature.diameter}
    case 'sideSpeakerGrill':
      return {
        ...base,
        width: feature.width,
        height: feature.height,
        cornerRadius: feature.cornerRadius,
      }
    case 'chargingConnector':
      return {...base, width: feature.width, height: feature.height, cornerRadius: feature.cornerRadius}
    case 'simCardSlot':
      return {
        ...base,
        width: feature.width,
        height: feature.height,
        cornerRadius: feature.cornerRadius,
        pinHoleOffset: feature.pinHoleOffset,
      }
  }
}

// ── Slider range limits ───────────────────────────────────────────

const RANGES = {
  position: {min: -80, max: 80, step: 0.1},
  width: {min: 0.5, max: 30, step: 0.1},
  height: {min: 0.3, max: 30, step: 0.1},
  cornerRadius: {min: 0, max: 10, step: 0.1},
  antennaWidth: {min: 0.3, max: 5, step: 0.1},
  diameter: {min: 0.5, max: 5, step: 0.1},
} as const

// ── Feature controls per type ─────────────────────────────────────

interface SideFeatureControlsProps {
  feature: SideEdgeFeatureConfig
  edge: SideEdge
  unit: string
  deviceWidth: number
  deviceHeight: number
  deviceDepth: number
  deviceCornerRadius: number
  onUpdate: (changes: Partial<SideEdgeFeatureConfig>) => void
  /** Committed callback — dispatches only on slider release (triggers worker) */
  onUpdateCommitted?: (changes: Partial<SideEdgeFeatureConfig>) => void
}

const SideFeatureControls = ({
  feature,
  edge,
  unit,
  deviceWidth,
  deviceHeight,
  deviceDepth,
  deviceCornerRadius,
  onUpdate,
  onUpdateCommitted,
}: SideFeatureControlsProps) => {
  const {t} = useTranslation()

  // Edge length and position limits depend on the edge
  const isHorizontalEdge = edge === 'left' || edge === 'right'
  const edgeLength = isHorizontalEdge ? deviceHeight : deviceWidth
  const maxPos = Math.round((edgeLength / 2 - deviceCornerRadius) * 10) / 10

  // Helper to build committed handler for a single field
  const committed = onUpdateCommitted ? (key: string) => (v: number) => onUpdateCommitted({[key]: v}) : undefined

  const posRow = (
    <SliderRow
      label={t('device.sideEdges.position')}
      value={feature.position}
      min={-maxPos}
      max={maxPos}
      step={0.1}
      unit={unit}
      onChange={(v: number) => onUpdate({position: v})}
      onChangeCommitted={committed?.('position')}
    />
  )

  switch (feature.type) {
    case 'sideButton': {
      const btnPosMax = Math.round((edgeLength / 2 - deviceCornerRadius - feature.height / 2) * 10) / 10
      return (
        <>
          <SliderRow
            label={t('device.sideEdges.position')}
            value={feature.position}
            min={-btnPosMax}
            max={btnPosMax}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({position: v})}
            onChangeCommitted={committed?.('position')}
          />
          <SliderRow
            label={t('device.sideEdges.width')}
            value={feature.width}
            min={0.5}
            max={Math.round(Math.max(0.5, deviceDepth) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({width: v})}
            onChangeCommitted={committed?.('width')}
          />
          <SliderRow
            label={t('device.sideEdges.height')}
            value={feature.height}
            min={0.3}
            max={Math.round((edgeLength - 2 * deviceCornerRadius) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({height: v})}
            onChangeCommitted={committed?.('height')}
          />
          <SliderRow
            label={t('device.sideEdges.cornerRadius')}
            value={feature.cornerRadius}
            min={0}
            max={Math.round((Math.min(feature.width, feature.height) / 2) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({cornerRadius: v})}
            onChangeCommitted={committed?.('cornerRadius')}
          />
          <SliderRow
            label={t('device.sideEdges.depth')}
            value={feature.depth ?? 0.5}
            min={-SIDE_BUTTON_DEPTH + 0.01}
            max={SIDE_BUTTON_DEPTH}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({depth: v})}
            onChangeCommitted={committed?.('depth')}
          />
          <SliderRow
            label={t('device.sideEdges.chamfer')}
            value={feature.chamfer ?? 0}
            min={0}
            max={Math.round(Math.min(0.5, feature.width / 3, feature.height / 3) * 100) / 100}
            step={0.05}
            unit={unit}
            onChange={(v: number) => onUpdate({chamfer: v})}
            onChangeCommitted={committed?.('chamfer')}
          />
        </>
      )
    }
    case 'muteSwitch': {
      const msBtnPosMax = Math.round((edgeLength / 2 - deviceCornerRadius - feature.height / 2) * 10) / 10
      return (
        <>
          <SliderRow
            label={t('device.sideEdges.position')}
            value={feature.position}
            min={-msBtnPosMax}
            max={msBtnPosMax}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({position: v})}
            onChangeCommitted={committed?.('position')}
          />
          <SliderRow
            label={t('device.sideEdges.width')}
            value={feature.width}
            min={0.5}
            max={Math.round(Math.max(0.5, deviceDepth) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({width: v})}
            onChangeCommitted={committed?.('width')}
          />
          <SliderRow
            label={t('device.sideEdges.height')}
            value={feature.height}
            min={0.3}
            max={Math.round((edgeLength - 2 * deviceCornerRadius) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({height: v})}
            onChangeCommitted={committed?.('height')}
          />
          <SliderRow
            label={t('device.sideEdges.cornerRadius')}
            value={feature.cornerRadius}
            min={0}
            max={Math.round((Math.min(feature.width, feature.height) / 2) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({cornerRadius: v})}
            onChangeCommitted={committed?.('cornerRadius')}
          />
        </>
      )
    }
    case 'antenna':
      return (
        <>
          {posRow}
          <SliderRow
            label={t('device.sideEdges.width')}
            value={feature.width}
            {...RANGES.antennaWidth}
            unit={unit}
            onChange={(v: number) => onUpdate({width: v})}
            onChangeCommitted={committed?.('width')}
          />
        </>
      )
    case 'screw':
      return (
        <>
          {posRow}
          <SliderRow
            label={t('device.sideEdges.diameter')}
            value={feature.diameter}
            min={0.5}
            max={Math.round(Math.max(0.5, deviceDepth) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({diameter: v})}
            onChangeCommitted={committed?.('diameter')}
          />
        </>
      )
    case 'sideSpeakerGrill': {
      // width = across/into-device (bounded by depth), height = along-edge (bounded by edge length)
      const grillPosMax = Math.round((edgeLength / 2 - deviceCornerRadius - feature.height / 2) * 10) / 10
      return (
        <>
          <SliderRow
            label={t('device.sideEdges.position')}
            value={feature.position}
            min={-grillPosMax}
            max={grillPosMax}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({position: v})}
            onChangeCommitted={committed?.('position')}
          />
          <SliderRow
            label={t('device.sideEdges.width')}
            value={feature.width}
            min={0.5}
            max={Math.round(Math.max(0.5, deviceDepth) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({width: v})}
            onChangeCommitted={committed?.('width')}
          />
          <SliderRow
            label={t('device.sideEdges.height')}
            value={feature.height}
            min={0.3}
            max={Math.round((edgeLength - 2 * deviceCornerRadius) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({height: v})}
            onChangeCommitted={committed?.('height')}
          />
          <SliderRow
            label={t('device.sideEdges.cornerRadius')}
            value={feature.cornerRadius}
            min={0}
            max={Math.round((Math.min(feature.width, feature.height) / 2) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({cornerRadius: v})}
            onChangeCommitted={committed?.('cornerRadius')}
          />
        </>
      )
    }
    case 'chargingConnector': {
      const connPosMax = Math.round((edgeLength / 2 - deviceCornerRadius - feature.width / 2) * 10) / 10
      const maxCr = Math.round((Math.min(feature.width, feature.height) / 2) * 10) / 10
      return (
        <>
          <SliderRow
            label={t('device.sideEdges.position')}
            value={feature.position}
            min={-connPosMax}
            max={connPosMax}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({position: v})}
            onChangeCommitted={committed?.('position')}
          />
          <SliderRow
            label={t('device.sideEdges.width')}
            value={feature.width}
            min={0.5}
            max={Math.round((edgeLength - 2 * deviceCornerRadius) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({width: v})}
            onChangeCommitted={committed?.('width')}
          />
          <SliderRow
            label={t('device.sideEdges.height')}
            value={feature.height}
            min={0.5}
            max={Math.round(Math.max(0.5, deviceDepth) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({height: v})}
            onChangeCommitted={committed?.('height')}
          />
          <SliderRow
            label={t('device.sideEdges.cornerRadius')}
            value={feature.cornerRadius}
            min={0}
            max={maxCr}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({cornerRadius: v})}
            onChangeCommitted={committed?.('cornerRadius')}
          />
        </>
      )
    }
    case 'simCardSlot': {
      const slotPosMax = Math.round((edgeLength / 2 - deviceCornerRadius - feature.height / 2) * 10) / 10
      const maxCr = Math.round((Math.min(feature.width, feature.height) / 2) * 10) / 10
      const pinMax = Math.round((feature.height / 2 - 1) * 10) / 10
      return (
        <>
          <SliderRow
            label={t('device.sideEdges.position')}
            value={feature.position}
            min={-slotPosMax}
            max={slotPosMax}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({position: v})}
            onChangeCommitted={committed?.('position')}
          />
          <SliderRow
            label={t('device.sideEdges.width')}
            value={feature.width}
            min={0.5}
            max={Math.round(Math.max(0.5, deviceDepth) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({width: v})}
            onChangeCommitted={committed?.('width')}
          />
          <SliderRow
            label={t('device.sideEdges.height')}
            value={feature.height}
            min={2}
            max={Math.round((edgeLength - 2 * deviceCornerRadius) * 10) / 10}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({height: v})}
            onChangeCommitted={committed?.('height')}
          />
          <SliderRow
            label={t('device.sideEdges.cornerRadius')}
            value={feature.cornerRadius}
            min={0}
            max={maxCr}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({cornerRadius: v})}
            onChangeCommitted={committed?.('cornerRadius')}
          />
          <SliderRow
            label={t('device.sideEdges.pinHoleOffset')}
            value={feature.pinHoleOffset}
            min={-pinMax}
            max={pinMax}
            step={0.1}
            unit={unit}
            onChange={(v: number) => onUpdate({pinHoleOffset: v})}
            onChangeCommitted={committed?.('pinHoleOffset')}
          />
        </>
      )
    }
  }
}

// ── EdgeSection — one edge accordion with its features ────────────

interface EdgeSectionProps {
  edge: SideEdge
  features: SideEdgeFeatureConfig[]
  decals: CustomDecal[]
  unit: string
  deviceWidth: number
  deviceHeight: number
  deviceDepth: number
  deviceEdgeBevel: number
  deviceCornerRadius: number
  onAdd: (edge: SideEdge, featureType: SideEdgeFeatureType) => void
  onRemove: (edge: SideEdge, id: string) => void
  onUpdate: (edge: SideEdge, id: string, changes: Partial<SideEdgeFeatureConfig>) => void
  onPreview: (edge: SideEdge, feature: SideEdgeFeatureConfig, changes: Partial<SideEdgeFeatureConfig>) => void
  onAddDecal: (edge: SideEdge) => void
  onRemoveDecal: (id: string) => void
  /** id of the most-recently-added feature/decal — its accordion mounts expanded */
  newlyAddedId: string | null
}

const EdgeSection = ({
  edge,
  features,
  decals,
  unit,
  deviceWidth,
  deviceHeight,
  deviceDepth,
  deviceEdgeBevel,
  deviceCornerRadius,
  onAdd,
  onRemove,
  onUpdate,
  onPreview,
  onAddDecal,
  onRemoveDecal,
  newlyAddedId,
}: EdgeSectionProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const handleOpenMenu = useCallback((e: ReactMouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(e.currentTarget)
  }, [])
  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null)
  }, [])

  const handleAddFeature = useCallback(
    (featureType: SideEdgeFeatureType) => {
      onAdd(edge, featureType)
      setMenuAnchor(null)
    },
    [edge, onAdd],
  )

  const handleAddDecalClick = useCallback(() => {
    onAddDecal(edge)
    setMenuAnchor(null)
  }, [edge, onAddDecal])

  return (
    <Box sx={sx.groupCard}>
      <Accordion disableGutters square sx={sx.edgeAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.featureExpandIcon} />} sx={sx.edgeSummary}>
          <Typography sx={sx.groupLabel}>{t(`device.sideEdges.edges.${edge}`)}</Typography>
          <Typography sx={sx.featureCount}>{features.length + decals.length}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={sx.edgeDetails}>
          {features.map((feature) => (
            <Box key={feature.id} sx={sx.featureCard}>
              <Accordion disableGutters square defaultExpanded={feature.id === newlyAddedId} sx={sx.featureAccordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.featureExpandIcon} />} sx={sx.featureSummary}>
                  <Typography sx={sx.featureLabel}>{t(`device.sideEdges.featureTypes.${feature.type}`)}</Typography>
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={(e: ReactMouseEvent) => {
                      e.stopPropagation()
                      onRemove(edge, feature.id)
                    }}
                    onKeyDown={(e: KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation()
                        onRemove(edge, feature.id)
                      }
                    }}
                    sx={sx.deleteButton}
                  >
                    <DeleteIcon sx={{fontSize: 16}} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={sx.featureDetails}>
                  <SideFeatureControls
                    feature={feature}
                    edge={edge}
                    unit={unit}
                    deviceWidth={deviceWidth}
                    deviceHeight={deviceHeight}
                    deviceDepth={deviceDepth}
                    deviceCornerRadius={deviceCornerRadius}
                    onUpdate={(changes) => onPreview(edge, feature, changes)}
                    onUpdateCommitted={(changes) => onUpdate(edge, feature.id, changes)}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}

          {/* Custom decals on this edge — rendered as feature accordions */}
          {decals.map((decal) => (
            <Box key={decal.id} sx={sx.featureCard}>
              <Accordion disableGutters square defaultExpanded={decal.id === newlyAddedId} sx={sx.featureAccordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.featureExpandIcon} />} sx={sx.featureSummary}>
                  <Typography sx={sx.featureLabel}>{t('device.sideEdges.featureTypes.customDecal')}</Typography>
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={(e: ReactMouseEvent) => {
                      e.stopPropagation()
                      onRemoveDecal(decal.id)
                    }}
                    onKeyDown={(e: KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation()
                        onRemoveDecal(decal.id)
                      }
                    }}
                    sx={sx.deleteButton}
                  >
                    <DeleteIcon sx={{fontSize: 16}} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={sx.featureDetails}>
                  <CustomDecalItem
                    decal={decal}
                    unit={unit}
                    deviceWidth={deviceWidth}
                    deviceHeight={deviceHeight}
                    deviceDepth={deviceDepth}
                    deviceEdgeBevel={deviceEdgeBevel}
                    deviceCornerRadius={deviceCornerRadius}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}

          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleOpenMenu}
            sx={sx.addButton}
          >
            {t('device.sideEdges.addFeature')}
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            transformOrigin={{vertical: 'top', horizontal: 'center'}}
          >
            {SIDE_FEATURE_TYPES.map((featureType) => (
              <MenuItem key={featureType} onClick={() => handleAddFeature(featureType)} sx={sx.menuItem}>
                {t(`device.sideEdges.featureTypes.${featureType}`)}
              </MenuItem>
            ))}
            <MenuItem onClick={handleAddDecalClick} sx={sx.menuItem}>
              {t('device.sideEdges.featureTypes.customDecal')}
            </MenuItem>
          </Menu>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

// ── DeviceSideEdgesPanel — side edge features panel ───────────────

const DeviceSideEdgesPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()
  const sideEdgeFeatures = useAppSelector((s) => selectActiveScene(s).sideEdgeFeatures)
  const customDecals = useAppSelector((s) => selectActiveScene(s).customDecals)
  const deviceWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const deviceHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)
  const deviceEdgeBevel = useAppSelector((s) => selectActiveScene(s).deviceEdgeBevel)
  const deviceCornerRadius = useAppSelector((s) => selectActiveScene(s).deviceCornerRadius)

  // Track the most-recently-added feature/decal id so its accordion mounts expanded.
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null)

  const handleAddDecal = useCallback(
    (edge: SideEdge) => {
      const id = generateCustomDecalId()
      dispatch(addCustomDecal({placement: edge, id}))
      setNewlyAddedId(id)
    },
    [dispatch],
  )
  const handleRemoveDecal = useCallback((id: string) => dispatch(removeCustomDecal(id)), [dispatch])

  const handleAddFeature = useCallback(
    (edge: SideEdge, featureType: SideEdgeFeatureType) => {
      const id = `${featureType}-${edge}-${Date.now()}`
      const defaults = SIDE_FEATURE_DEFAULTS[featureType]
      dispatch(addSideEdgeFeature({edge, feature: {...defaults, id} as SideEdgeFeatureConfig}))
      setNewlyAddedId(id)
    },
    [dispatch],
  )

  const handleRemoveFeature = useCallback(
    (edge: SideEdge, id: string) => {
      // Capture the feature data before removal for the red ghost
      const feature = sideEdgeFeatures[edge].find((f) => f.id === id)
      if (feature) {
        setRemovedSideEdge(edge, id, {
          type: feature.type,
          edge,
          position: feature.position,
          width: 'width' in feature ? (feature as {width: number}).width : 0,
          height: 'height' in feature ? (feature as {height: number}).height : 0,
          cornerRadius: 'cornerRadius' in feature ? (feature as {cornerRadius: number}).cornerRadius : 0,
          diameter: 'diameter' in feature ? (feature as {diameter: number}).diameter : 0,
          depth: feature.type === 'sideButton' ? (feature.depth ?? 0.5) : 0,
          chamfer: feature.type === 'sideButton' ? (feature.chamfer ?? 0) : 0,
        })
      }
      dispatch(removeSideEdgeFeature({edge, id}))
    },
    [dispatch, sideEdgeFeatures],
  )

  const handleUpdateFeature = useCallback(
    (edge: SideEdge, id: string, changes: Partial<SideEdgeFeatureConfig>) => {
      // Preview is NOT cleared here — the ghost model clears it when the worker finishes
      dispatch(updateSideEdgeFeature({edge, id, changes}))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  /** Feed preview store during slider drag */
  const handlePreviewFeature = useCallback(
    (edge: SideEdge, feature: SideEdgeFeatureConfig, changes: Partial<SideEdgeFeatureConfig>) => {
      const base = featureToPreview(edge, feature)
      // Mirror the reducer's position clamp so the ghost reflects where the feature
      // will actually land when a size change shrinks the valid position range.
      const merged = {...feature, ...changes} as SideEdgeFeatureConfig
      const position = clampSideFeaturePosition(merged, edge, deviceWidth, deviceHeight, deviceCornerRadius)
      setPreviewSideEdge(edge, feature.id, base, {...changes, position} as Partial<PreviewSideEdge>)
    },
    [deviceWidth, deviceHeight, deviceCornerRadius],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const unit = t('device.shapes.unit')

  return (
    <Accordion disableGutters square sx={sx.subAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.subSummary}>
        <BorderAllIcon sx={sx.subIcon} />
        <Typography sx={sx.subTitle}>{t('device.sideEdges.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.subDetails}>
        {EDGES.map((edge) => (
          <EdgeSection
            key={edge}
            edge={edge}
            features={sideEdgeFeatures[edge]}
            decals={customDecals.filter((d) => d.placement === edge)}
            unit={unit}
            deviceWidth={deviceWidth}
            deviceHeight={deviceHeight}
            deviceDepth={deviceDepth}
            deviceEdgeBevel={deviceEdgeBevel}
            deviceCornerRadius={deviceCornerRadius}
            onAdd={handleAddFeature}
            onRemove={handleRemoveFeature}
            onUpdate={handleUpdateFeature}
            onPreview={handlePreviewFeature}
            onAddDecal={handleAddDecal}
            onRemoveDecal={handleRemoveDecal}
            newlyAddedId={newlyAddedId}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

// ── Styles — outer sub-accordion ──────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  subAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  subSummary: {
    minHeight: 40,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 40},
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
      gap: '6px',
      '&.Mui-expanded': {margin: '8px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 18,
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  subDetails: {
    padding: '0 4px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  groupCard: {
    borderRadius: '6px',
    border: `1px solid ${tokens.accordion.border}`,
    overflow: 'hidden',
  },
  featureCard: {
    borderRadius: '4px',
    border: `1px solid ${tokens.accordion.border}`,
    overflow: 'hidden',
    marginBottom: '4px',
  },
  edgeAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  edgeSummary: {
    minHeight: 32,
    padding: '0 8px',
    '&.Mui-expanded': {minHeight: 32},
    '& .MuiAccordionSummary-content': {
      margin: '4px 0',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&.Mui-expanded': {margin: '4px 0'},
    },
  },
  edgeDetails: {
    padding: '0 8px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  featureAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  featureSummary: {
    minHeight: 28,
    padding: '0 6px',
    '&.Mui-expanded': {minHeight: 28},
    '& .MuiAccordionSummary-content': {
      margin: '2px 0',
      alignItems: 'center',
      justifyContent: 'space-between',
      '&.Mui-expanded': {margin: '2px 0'},
    },
  },
  featureExpandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 16,
  },
  featureDetails: {
    padding: '0 6px 6px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  groupLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: tokens.text.primary,
  },
  featureLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: tokens.text.primary,
  },
  featureCount: {
    fontSize: 10,
    fontWeight: 500,
    color: tokens.text.muted,
    backgroundColor: tokens.accordion.border,
    borderRadius: '8px',
    padding: '0 6px',
    lineHeight: '18px',
  },
  deleteButton: {
    color: tokens.text.muted,
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
    '&:hover': {
      color: '#ef5350',
    },
  },
  addButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    color: tokens.text.primary,
    borderColor: tokens.accordion.border,
    marginTop: '4px',
    '&:hover': {
      borderColor: tokens.text.secondary,
    },
  },
  menuItem: {
    fontSize: 13,
  },
})

export default DeviceSideEdgesPanel
